import { X, Edit2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PlanPackage } from '../types';
import { StatusBadge, formatCurrency } from './StatusBadge';

interface PlanDetailModalProps {
    isOpen: boolean;
    plan: PlanPackage | null;
    onClose: () => void;
    onEdit: () => void;
}

export const PlanDetailModal = ({ isOpen, plan, onClose, onEdit }: PlanDetailModalProps) => {
    const { t } = useTranslation();

    if (!isOpen || !plan) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 rounded-md shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 z-10">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">{plan.name}</h3>
                        <StatusBadge isActive={plan.is_active} />
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Description */}
                    <div>
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('plan.detail.mainInfo')}</h4>
                        <p className="text-gray-600 dark:text-gray-400">{plan.description || t('plan.detail.noDescription')}</p>
                    </div>

                    {/* Key Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-md">
                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('plan.detail.price')}</p>
                            <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                                {formatCurrency(plan.price)}
                            </p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-md">
                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('plan.detail.profit')}</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                                {parseFloat(plan.min_profit_rate) > 0 ? `${plan.min_profit_rate}${t('plan.detail.perMonth')}` : t('plan.detail.byRevenue')}
                            </p>
                            {parseFloat(plan.high_profit_rate) > 0 && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {t('plan.detail.high')}: {plan.high_profit_rate}% ({plan.high_rate_duration} {t('plan.detail.firstMonths')})
                                </p>
                            )}
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-md">
                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('plan.detail.duration')}</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                                {plan.duration_months === 0 ? t('plan.detail.untilCancel') : `${plan.duration_months} ${t('plan.detail.months')}`}
                            </p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-md">
                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('plan.detail.commissionF1')}</p>
                            <p className="text-lg font-bold text-blue-600 dark:text-blue-400 mt-1">
                                {plan.commission_rate}%
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                {t('plan.detail.fromMonth')} {plan.commission_start_month}
                            </p>
                        </div>
                    </div>

                    {/* Additional Info */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-md">
                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('plan.detail.monthlyCommission')}</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                                {plan.monthly_commission_rate}%
                            </p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-md">
                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('plan.detail.partPerUser')}</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                                {plan.part_per_user}
                            </p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-md">
                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('plan.detail.tax')}</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                                {plan.tax_rate}%
                            </p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-md">
                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('plan.detail.slot')}</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                                {plan.sold_slots || 0}/{plan.total_slots}
                            </p>
                        </div>
                    </div>

                    {/* Slots Progress */}
                    <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-md">
                        <div className="flex justify-between items-center mb-2">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('plan.detail.slotsSold')}</p>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">
                                {plan.sold_slots || 0}/{plan.total_slots}
                            </p>
                        </div>
                        <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                            <div
                                className="h-full bg-blue-600 rounded-full"
                                style={{ width: `${((plan.sold_slots || 0) / plan.total_slots) * 100}%` }}
                            />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {t('plan.detail.remaining', { count: plan.total_slots - (plan.sold_slots || 0) })}
                        </p>
                    </div>

                    {/* Phases */}
                    {plan.phases && plan.phases.length > 0 && (
                        <div>
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{t('plan.detail.phasesTitle')}</h4>
                            <div className="space-y-3">
                                {plan.phases.map((phase, index) => (
                                    <div key={index} className="flex gap-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-md">
                                        <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                                            {phase.phase_number}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h5 className="font-semibold text-gray-900 dark:text-white">{phase.title}</h5>
                                                <span className="text-xs text-gray-500 dark:text-gray-400">{phase.duration}</span>
                                            </div>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">{phase.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Benefits */}
                    {plan.benefits && plan.benefits.length > 0 && (
                        <div>
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{t('plan.detail.benefitsTitle')}</h4>
                            <ul className="space-y-2">
                                {plan.benefits.map((benefit, index) => (
                                    <li key={index} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                                        <span className="text-emerald-500 mt-0.5">✓</span>
                                        {benefit.description}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                    <button
                        onClick={onEdit}
                        className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-md transition flex items-center"
                    >
                        <Edit2 className="h-4 w-4 mr-2" />
                        {t('plan.detail.edit')}
                    </button>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        {t('plan.detail.close')}
                    </button>
                </div>
            </div>
        </div>
    );
};
